<template>
  <div>
    <b-tabs v-model="activeTab" content-class="mt-3">

      <!-- ── Tab 1: Reference Levels ─────────────────────────────────────── -->
      <b-tab title="Reference Levels">
        <b-card no-body>
          <div class="m-2">
            <p class="text-muted mb-0">
              Upload the 5 official Hygicult TPC reference strip images. All 5 must be configured
              before the AI can classify customer submissions.
            </p>
          </div>

          <b-overlay :show="levelsLoading" rounded="sm">
            <div class="p-2">
              <b-row>
                <b-col
                  v-for="lvl in levels"
                  :key="lvl.level"
                  cols="12" md="6" lg="4"
                  class="mb-3">
                  <b-card :border-variant="levelVariant(lvl.level)" class="h-100">
                    <div class="d-flex align-items-center mb-2">
                      <b-badge :variant="levelVariant(lvl.level)" class="mr-2" style="font-size:1rem;padding:6px 12px;">
                        Level {{ lvl.level }}
                      </b-badge>
                      <span class="text-muted small">{{ levelLabel(lvl.level) }}</span>
                    </div>

                    <div
                      class="text-center mb-2 border rounded"
                      style="background:#f8f9fa;min-height:120px;display:flex;align-items:center;justify-content:center;">
                      <img
                        v-if="lvl.reference_image_url"
                        :src="lvl.reference_image_url"
                        style="max-height:120px;max-width:100%;border-radius:4px;"
                        alt="Reference strip"
                      />
                      <span v-else class="text-muted small">No image uploaded</span>
                    </div>

                    <b-form @submit.prevent="saveLevel(lvl)">
                      <b-form-group label="Liquid CFU" label-size="sm">
                        <b-form-input v-model="lvl.liquid_cfu" size="sm" required />
                      </b-form-group>
                      <b-form-group label="Surface CFU" label-size="sm">
                        <b-form-input v-model="lvl.surface_cfu" size="sm" required />
                      </b-form-group>
                      <b-form-group label="Recommendation" label-size="sm">
                        <b-form-textarea v-model="lvl.recommendation" rows="2" size="sm" />
                      </b-form-group>
                      <b-form-group label="Reference Image" label-size="sm">
                        <b-form-file
                          v-model="lvl._imageFile"
                          accept="image/*"
                          size="sm"
                          placeholder="Choose image…"
                        />
                      </b-form-group>
                      <b-button
                        type="submit"
                        :variant="levelVariant(lvl.level)"
                        size="sm"
                        block
                        :disabled="lvl._saving">
                        {{ lvl._saving ? 'Saving…' : 'Save Level ' + lvl.level }}
                      </b-button>
                    </b-form>

                    <div v-if="lvl.coverage_pct !== null" class="mt-2 text-muted small text-right">
                      Calibrated coverage: {{ lvl.coverage_pct }}%
                    </div>
                  </b-card>
                </b-col>
              </b-row>
            </div>
          </b-overlay>
        </b-card>
      </b-tab>

      <!-- ── Tab 2: Submissions ──────────────────────────────────────────── -->
      <b-tab title="Submissions">
        <b-card no-body>
          <div class="m-2 d-flex flex-wrap gap-2 align-items-center justify-content-between">
            <div class="d-flex flex-wrap gap-2">
              <b-form-select v-model="filterMethod"   :options="methodOptions"   size="sm" style="width:140px;" @change="loadSubmissions" />
              <b-form-select v-model="filterLevel"    :options="levelOptions"    size="sm" style="width:110px;" @change="loadSubmissions" />
              <b-form-select v-model="filterBirdType" :options="birdTypeOptions" size="sm" style="width:120px;" @change="loadSubmissions" />
            </div>
            <span class="text-muted small">{{ totalSubmissions }} total</span>
          </div>

          <b-table
            responsive
            :items="submissions"
            :fields="submissionFields"
            :busy="submissionsLoading"
            show-empty
            empty-text="No submissions yet."
            small
          >
            <template #table-busy>
              <div class="text-center my-3"><b-spinner variant="primary" /></div>
            </template>

            <template #cell(image_url)="{ item }">
              <a v-if="item.image_url" :href="item.image_url" target="_blank">
                <img :src="item.image_url" style="height:44px;width:44px;object-fit:cover;border-radius:4px;" alt="slide" />
              </a>
              <span v-else class="text-muted">—</span>
            </template>

            <template #cell(level)="{ item }">
              <b-badge v-if="item.level" :style="levelBadgeStyle(item.level)">
                L{{ item.level }}
              </b-badge>
              <span v-else class="text-muted">—</span>
              <span v-if="item.second_level && item.second_level !== item.level" class="text-muted small ml-1">
                or L{{ item.second_level }}?
              </span>
            </template>

            <template #cell(classification_method)="{ item }">
              <b-badge :variant="methodBadgeVariant(item.classification_method)">
                {{ methodBadgeLabel(item.classification_method) }}
              </b-badge>
            </template>

            <template #cell(bird_type)="{ item }">
              <b-badge :variant="birdBadgeVariant(item.bird_type)" pill>
                {{ item.bird_type }}
              </b-badge>
            </template>

            <template #cell(override)="{ item }">
              <div class="d-flex align-items-center gap-1">
                <b-form-select
                  v-model="item._overrideLevel"
                  :options="overrideLevelOptions"
                  size="sm"
                  style="width:80px;"
                />
                <b-button
                  size="sm"
                  variant="outline-warning"
                  :disabled="!item._overrideLevel || item._overriding"
                  @click="applyOverride(item)">
                  {{ item._overriding ? '…' : 'Set' }}
                </b-button>
              </div>
            </template>
          </b-table>

          <div class="d-flex justify-content-center my-2" v-if="totalSubmissions > 30">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalSubmissions"
              :per-page="30"
              size="sm"
              @change="loadSubmissions"
            />
          </div>
        </b-card>
      </b-tab>

      <!-- ── Tab 3: Analytics ───────────────────────────────────────────── -->
      <b-tab title="Analytics">
        <b-card no-body>
          <div class="m-2 d-flex align-items-center gap-2">
            <b-form-select v-model="analyticsPeriod" :options="periodOptions" size="sm" style="width:130px;" @change="loadAnalytics" />
          </div>

          <b-overlay :show="analyticsLoading" rounded="sm">
            <div class="p-3" v-if="analytics">

              <!-- ── KPI row ── -->
              <b-row class="mb-3">
                <b-col cols="6" md="3" class="mb-2">
                  <b-card class="text-center" bg-variant="light">
                    <h3 class="mb-0">{{ analytics.total }}</h3>
                    <small class="text-muted">Total Tests</small>
                  </b-card>
                </b-col>
                <b-col cols="6" md="3" class="mb-2">
                  <b-card class="text-center" bg-variant="light">
                    <h3 class="mb-0">{{ (analytics.avg_confidence * 100).toFixed(0) }}%</h3>
                    <small class="text-muted">Avg AI Confidence</small>
                  </b-card>
                </b-col>
                <b-col cols="6" md="3" class="mb-2">
                  <b-card class="text-center" bg-variant="light">
                    <h3 class="mb-0">{{ analytics.stage_dist.post || 0 }}</h3>
                    <small class="text-muted">Post-Cleaning Tests</small>
                  </b-card>
                </b-col>
                <b-col cols="6" md="3" class="mb-2">
                  <b-card class="text-center" bg-variant="light">
                    <h3 class="mb-0">{{ analytics.method_dist.gemini || 0 }}</h3>
                    <small class="text-muted">AI Classified</small>
                  </b-card>
                </b-col>
              </b-row>

              <!-- ── Level distribution ── -->
              <h6 class="mt-1 mb-2">Contamination Level Distribution</h6>
              <div class="d-flex gap-2 align-items-flex-end mb-4" style="height:80px;">
                <div v-for="n in [1,2,3,4,5]" :key="n" class="text-center" style="flex:1;">
                  <div :style="{height: levelBarHeight(n) + 'px', backgroundColor: levelColors[n], borderRadius: '4px 4px 0 0'}" />
                  <small>L{{ n }}: {{ analytics.level_dist[n] || 0 }}</small>
                </div>
              </div>

              <!-- ── Bird Type Distribution ── -->
              <h6 class="mt-2 mb-2">Bird Type Distribution</h6>
              <b-row class="mb-4">
                <b-col
                  v-for="(count, type) in analytics.bird_type_dist"
                  :key="type"
                  cols="4" md="2"
                  class="mb-2">
                  <b-card class="text-center p-1" :style="{ borderTop: '3px solid ' + birdColor(type) }">
                    <h4 class="mb-0">{{ count }}</h4>
                    <small>{{ type }}</small>
                  </b-card>
                </b-col>
              </b-row>

              <!-- ── State-wise Distribution ── -->
              <h6 class="mt-2 mb-2">State-wise Tests (Top 10)</h6>
              <b-table
                :items="stateDistItems"
                :fields="[{key:'state_name', label:'State'}, {key:'count', label:'Tests', sortable:true}]"
                small
                responsive
                sort-by="count"
                sort-desc
                class="mb-0"
              >
                <template #cell(count)="{ item }">
                  <div class="d-flex align-items-center gap-2">
                    <div
                      style="height:10px;background:#4f81bd;border-radius:2px;"
                      :style="{width: Math.round((item.count / maxStateCount) * 100) + 'px'}"
                    />
                    {{ item.count }}
                  </div>
                </template>
              </b-table>

            </div>
            <div v-else class="p-4 text-center text-muted">No analytics data available.</div>
          </b-overlay>
        </b-card>
      </b-tab>

    </b-tabs>
  </div>
</template>

<script>
import axios from '@axios'

const LEVEL_COLORS = {
  1: '#22c55e', 2: '#84cc16', 3: '#eab308', 4: '#f97316', 5: '#ef4444',
}
const LEVEL_LABELS = {
  1: 'Very Low', 2: 'Low', 3: 'Moderate', 4: 'High', 5: 'Very High',
}

export default {
  name: 'HygicultAdmin',

  data() {
    return {
      activeTab: 0,

      levels:        [],
      levelsLoading: false,
      levelColors: LEVEL_COLORS,

      submissions:        [],
      submissionsLoading: false,
      totalSubmissions:   0,
      currentPage:        1,
      filterMethod:       '',
      filterLevel:        '',
      filterBirdType:     '',

      methodOptions: [
        { value: '',         text: 'All methods' },
        { value: 'gemini',   text: 'AI' },
        { value: 'coverage', text: 'Coverage' },
        { value: 'manual',   text: 'Manual' },
      ],
      levelOptions: [
        { value: '', text: 'All levels' },
        ...[1,2,3,4,5].map(n => ({ value: n, text: `Level ${n}` })),
      ],
      birdTypeOptions: [
        { value: '',         text: 'All types' },
        { value: 'Broiler',  text: 'Broiler' },
        { value: 'Breeder',  text: 'Breeder' },
        { value: 'Layer',    text: 'Layer' },
      ],
      overrideLevelOptions: [
        { value: null, text: 'Override…' },
        ...[1,2,3,4,5].map(n => ({ value: n, text: `Level ${n}` })),
      ],
      submissionFields: [
        { key: 'image_url',             label: 'Slide' },
        { key: 'company_name',          label: 'Company' },
        { key: 'farm_name',             label: 'Farm' },
        { key: 'shed',                  label: 'Shed' },
        { key: 'bird_type',             label: 'Type' },
        { key: 'state_name',            label: 'State' },
        { key: 'cleaning_stage',        label: 'Stage' },
        { key: 'sample_type',           label: 'Sample' },
        { key: 'level',                 label: 'Level' },
        { key: 'cfu_value',             label: 'CFU' },
        { key: 'classification_method', label: 'Method' },
        { key: 'created_at',            label: 'Date', sortable: true, formatter: v => v ? new Date(v).toLocaleDateString() : '—' },
        { key: 'override',              label: 'Override' },
      ],

      analytics:        null,
      analyticsLoading: false,
      analyticsPeriod:  'month',
      periodOptions: [
        { value: 'week',    text: 'This week' },
        { value: 'month',   text: 'This month' },
        { value: 'quarter', text: 'This quarter' },
        { value: 'year',    text: 'This year' },
      ],
    }
  },

  computed: {
    stateDistItems() {
      if (!this.analytics?.state_dist) return []
      return Object.entries(this.analytics.state_dist)
        .map(([state_name, count]) => ({ state_name, count }))
        .sort((a, b) => b.count - a.count)
    },
    maxStateCount() {
      if (!this.stateDistItems.length) return 1
      return Math.max(...this.stateDistItems.map(i => i.count), 1)
    },
  },

  created() {
    this.loadLevels()
    this.loadSubmissions()
    this.loadAnalytics()
  },

  methods: {
    async loadLevels() {
      this.levelsLoading = true
      try {
        const res = await axios.get('/web/hygicult/levels')
        this.levels = (res.data?.levels ?? []).map(l => ({ ...l, _imageFile: null, _saving: false }))
      } catch {
        this.$bvToast.toast('Failed to load reference levels', { variant: 'danger', title: 'Error' })
      } finally {
        this.levelsLoading = false
      }
    },

    async saveLevel(lvl) {
      this.$set(lvl, '_saving', true)
      try {
        const fd = new FormData()
        fd.append('liquid_cfu',     lvl.liquid_cfu)
        fd.append('surface_cfu',    lvl.surface_cfu)
        fd.append('recommendation', lvl.recommendation ?? '')
        if (lvl._imageFile) fd.append('image', lvl._imageFile)

        const res = await axios.post(`/web/hygicult/levels/${lvl.level}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        const updated = res.data?.level ?? {}
        this.$set(lvl, 'reference_image_url', updated.reference_image_url ?? lvl.reference_image_url)
        this.$set(lvl, 'coverage_pct',        updated.coverage_pct)
        this.$set(lvl, '_imageFile',           null)
        this.$bvToast.toast(`Level ${lvl.level} saved`, { variant: 'success', title: 'Saved' })
      } catch (e) {
        this.$bvToast.toast(e.response?.data?.message ?? 'Save failed', { variant: 'danger', title: 'Error' })
      } finally {
        this.$set(lvl, '_saving', false)
      }
    },

    async loadSubmissions() {
      this.submissionsLoading = true
      try {
        const params = { page: this.currentPage }
        if (this.filterMethod)   params.method    = this.filterMethod
        if (this.filterLevel)    params.level     = this.filterLevel
        if (this.filterBirdType) params.bird_type = this.filterBirdType
        const res = await axios.get('/web/hygicult/submissions', { params })
        this.submissions      = (res.data?.data ?? []).map(s => ({ ...s, _overrideLevel: null, _overriding: false }))
        this.totalSubmissions = res.data?.total ?? this.submissions.length
      } catch {
        this.$bvToast.toast('Failed to load submissions', { variant: 'danger', title: 'Error' })
      } finally {
        this.submissionsLoading = false
      }
    },

    async applyOverride(item) {
      if (!item._overrideLevel) return
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Override level for submission #${item.id} to Level ${item._overrideLevel}?`,
        { title: 'Confirm Override', okVariant: 'warning', okTitle: 'Override' },
      )
      if (!confirmed) return

      this.$set(item, '_overriding', true)
      try {
        const res = await axios.post(`/web/hygicult/submissions/${item.id}/override`, { level: item._overrideLevel })
        const updated = res.data?.submission ?? {}
        this.$set(item, 'level',                 updated.level)
        this.$set(item, 'cfu_value',             updated.cfu_value)
        this.$set(item, 'classification_method', updated.classification_method)
        this.$set(item, '_overrideLevel',        null)
        this.$bvToast.toast(`Level overridden to L${updated.level}`, { variant: 'success', title: 'Updated' })
      } catch (e) {
        this.$bvToast.toast(e.response?.data?.message ?? 'Override failed', { variant: 'danger', title: 'Error' })
      } finally {
        this.$set(item, '_overriding', false)
      }
    },

    async loadAnalytics() {
      this.analyticsLoading = true
      try {
        const res = await axios.get('/web/hygicult/analytics', { params: { period: this.analyticsPeriod } })
        this.analytics = res.data
      } catch {
        this.$bvToast.toast('Failed to load analytics', { variant: 'danger', title: 'Error' })
      } finally {
        this.analyticsLoading = false
      }
    },

    levelBarHeight(n) {
      const count = this.analytics?.level_dist?.[n] ?? 0
      const max   = Math.max(...Object.values(this.analytics?.level_dist ?? { 0: 1 }), 1)
      return Math.round((count / max) * 60) + 4
    },

    levelVariant(n) {
      return { 1: 'success', 2: 'success', 3: 'warning', 4: 'warning', 5: 'danger' }[n] ?? 'secondary'
    },
    levelLabel(n) { return LEVEL_LABELS[n] ?? '' },
    levelBadgeStyle(n) {
      return `background-color:${LEVEL_COLORS[n] ?? '#888'};color:#fff;`
    },
    methodBadgeVariant(method) {
      return { gemini: 'primary', manual: 'warning', coverage: 'secondary' }[method] ?? 'light'
    },
    methodBadgeLabel(method) {
      return { gemini: 'AI', manual: 'Manual', coverage: 'Coverage' }[method] ?? method
    },
    birdBadgeVariant(type) {
      return { Broiler: 'warning', Breeder: 'info', Layer: 'success' }[type] ?? 'secondary'
    },
    birdColor(type) {
      return { Broiler: '#f97316', Breeder: '#3b82f6', Layer: '#22c55e' }[type] ?? '#888'
    },
  },
}
</script>
